import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { getAuditLogDetails } from "../../../redux/slices";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { getAuditLogHeaderDetails } from "../../../helpers/accounting-helper";
import {
  DATE_TIME_FORMATS,
  EDITED_AUDITS,
  SALES_AUDIT_OPTIONS_MAP,
} from "../../../data/constants";
import moment from "moment";

interface Props {
  setInvoiceSummary?: any;
  fetchInvoiceDetails?: any;
  getPayload?: any;
  searchData?: any;
  selectedId: string;
  selectedSubType: string;
}

const AuditLogs = (props: Props) => {
  const {
    setInvoiceSummary,
    fetchInvoiceDetails,
    getPayload,
    searchData,
    selectedId,
    selectedSubType
  } = props;
  const BASE_STATE = {
    searchValue: "",
    lastKey: "",
    hasMoreDetails: true,
    loading: false,
    auditData: [],
  };
  const [gridViewRef, setGridViewRef] = useState<any>({ current: null });
  const dispatch = useDispatch();
  const [isDataPresent, setIsDataPresent] = useState<boolean>(false);
  const [state, setState] = useState<any>(BASE_STATE);
  const { isAtBottom } = useInfiniteScroll(gridViewRef);
  const [saleId, setSaleId] = useState<string>("");
  const auditLogData = useSelector(
    (state: RootState) => state.accountingReducer?.auditDetails
  );

  useEffect(() => {
    fetchAuditDetails();
  }, []);

  useEffect(() => {
    if (isAtBottom) {
      fetchAuditDetails();
    }
  }, [isAtBottom]);

  useEffect(() => {
    fetchAuditDetails();
  }, [state.searchValue]);

  useEffect(() => {
    setState({
      auditData: auditLogData?.results,
    });
  }, [auditLogData]);

  const callbackRef = useCallback((tempRef: any) => {
    if (!tempRef) return;
    setGridViewRef({ current: tempRef });
  }, []);

  function fetchAuditDetails() {
    let dataToPost = getPayload();
    if (selectedId === SALES_AUDIT_OPTIONS_MAP.allorders.id) {
      delete dataToPost?.auditType;
      delete dataToPost?.auditSubType;
    }
    if (selectedId && selectedId !== SALES_AUDIT_OPTIONS_MAP.allorders.id) {
      dataToPost["auditType"] = SALES_AUDIT_OPTIONS_MAP[selectedId].label;
      if (selectedSubType) {
        dataToPost["auditSubType"] = selectedSubType;
      }
    }

    //if last key is present that shows it has more details and on scroll it will call callback function to get more details
    if (state.hasMoreDetails) {
      setTimeout(() => {
        let newState = { ...state };
        const callback = (response: any) => {
          if (response.lastKey)
            newState = {
              ...newState,
              loading: false,
              hasMoreDetails: true,
              lastKey: response.lastKey,
            };
          else
            newState = {
              ...newState,
              loading: false,
              hasMoreDetails: false,
              lastKey: "",
            };
          if (response?.results) {
            newState = {
              ...newState,
              auditData: [...state.auditData, ...response.results],
            };
          }
          setState(newState);
          if (!isDataPresent && response.results) setIsDataPresent(true);
        };

        if (checkSearchStringLength()) {
          dataToPost.searchString = state.searchValue;
        }
        if (state.lastKey) {
          dataToPost.lastKey = state.lastKey;
        }
        dispatch(getAuditLogDetails({ dataToPost, callback }));
      }, 500);
    }
  }

  function checkSearchStringLength() {
    return state.searchValue.length >= 3;
  }

  function handleAuditDataChange(auditData: any) {
    setSaleId(auditData?.saleId);
    setInvoiceSummary(true);
    fetchInvoiceDetails(auditData?.saleId);
  }

  function renderAuditLogDetails(auditData:any, headerDetails:any, index:number): JSX.Element{
    const isTextPresent = EDITED_AUDITS.includes(selectedId);
    let typeHeaderText = SALES_AUDIT_OPTIONS_MAP[selectedId]?.headerText;
    let discountPercentage = Number(
      (-auditData?.netDiscountTotal / auditData?.billRoundedAmount) * 100
    ).toFixed(2);
    return (
      <div className="flex flex-col justify-center items-end flex-end">
        <div className="text-[14px] text-[#3A5E8B] flex smd:text-[13px]">
          <span>Bill Amount:</span>
          <span className="ml-5 font-semibold">
            ₹{Number(auditData?.billRoundedAmount).toFixed(2)}
          </span>
        </div>
        {headerDetails && typeHeaderText && (
          <div className="text-[14px]">
            <span>{typeHeaderText}:</span>
            {!isTextPresent ? (
              <>
                <span className="font-semibold ml-5 mr-2">{headerDetails[index]}</span>
                <span className="font-normal ">
                  {selectedId === SALES_AUDIT_OPTIONS_MAP.discount.id && (
                    <>({discountPercentage}%)</>
                  )}
                </span>
              </>
            ) : (
              <span>
                {headerDetails[index]}/{auditData?.totalTicketCount}
              </span>
            )}
          </div>
        )}
        <div>
          {" "}
          {selectedSubType.length > 0 &&
            (selectedId === SALES_AUDIT_OPTIONS_MAP.payment.id ||
              selectedId === SALES_AUDIT_OPTIONS_MAP.discount.id) &&
            headerDetails &&
            typeHeaderText && (
              <div className="text-[14px] text-[#39884F]">
                <span>
                  {typeHeaderText}
                  {selectedId === SALES_AUDIT_OPTIONS_MAP.payment.id
                    ? " by"
                    : " from"}{" "}
                  {selectedSubType}:
                </span>
                <span className="ml-5 font-semibold">
                  ₹
                  {selectedId === SALES_AUDIT_OPTIONS_MAP.payment.id
                    ? auditData?.paymentByMode
                    : auditData?.discountByVoucher}
                </span>
              </div>
            )}
        </div>
      </div>
    );
  }

  function renderAuditLogs(listToRender: any): JSX.Element {
    const headerDetails = getAuditLogHeaderDetails(selectedId, listToRender);
    return (
      <>
        {!!listToRender.length &&
          listToRender.map((auditData: any, index: number) => (
            <div
              className={`brand-item flex items-center sm:!py-[20px] sm:!px-[16px] tb:px-[24px] tb:py-[16px] relative cursor-pointer p-16  ${
                auditData.saleId === saleId
                  ? "bg-black/5 border-0 border-l-[4px] !border-black "
                  : " border-[#ECECEC] border-b-[1px] last:border-b"
              }`}
              key={index}
              onClick={() => {
                handleAuditDataChange(auditData);
              }}
            >
              <div className="flex w-[100%] justify-between pr-10">
                <div className="flex flex-col justify-center flex-start">
                <span className={`text-[14px] capitalize `}>
                  <span className="font-semibold">{auditData?.billNumber}</span>            
                     <span className="opacity-50 pl-8 smd:border-none  ml-5">
                {moment(auditData?.saleDate).format(
                  DATE_TIME_FORMATS.dayMonthTime
                )}
              </span>
                  </span>
                  <div className="flex my-4">
                    <div
                      className={`text-black text-opacity-70 text-sm sm:text-[12px] font-medium pr-3 gap-2 border-r-[1.5px]`}
                    >
                      <span>{auditData?.branchName}</span>
                    </div>
                    <div
                      className={`text-black text-opacity-70 text-sm sm:text-[12px] font-medium gap-2 pl-3`}
                    >
                      <span>{auditData?.channelName}</span>
                    </div>
                  </div>       
                  
                </div>
                {renderAuditLogDetails(auditData, headerDetails, index)}
              </div>
            </div>
          ))}
      </>
    );
  }

  return (
    <Box className="w-[100%]  h-[100%]">
      <div ref={callbackRef}>
        {searchData.value.length >= 3
          ? renderAuditLogs(searchData.results)
          : renderAuditLogs(state?.auditData || [])}
      </div>
    </Box>
  );
};

export default AuditLogs;
